import { useRef, useState } from "react";
import { Accordion, AccordionTab } from "primereact/accordion";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";
import { format } from "date-fns";
import { StudentHistoryDetailsResponse, StudentHistoryResponse } from "../../models/dtos";
import { studentsApi } from "../../services/students/studentsApi";
import StudentEventDialog from "./StudentEventDialog";

interface StudentHistorySectionProps {
  studentId: number;
  studentHistories: StudentHistoryResponse[];
}

const formatDetailsValue = (value: unknown) => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }

  if (typeof value === "object") {
    return <pre className="m-0">{JSON.stringify(value, null, 2)}</pre>;
  }

  return String(value);
};

const formatDateTime = (value: Date | string | null | undefined) => {
  if (!value) {
    return "-";
  }

  return format(new Date(value), "dd.MM.yyyy HH:mm");
};

const StudentHistorySection = ({ studentId, studentHistories }: StudentHistorySectionProps) => {
  const [showEventDialog, setShowEventDialog] = useState(false);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [selectedHistory, setSelectedHistory] = useState<StudentHistoryResponse | null>(null);
  const [historyDetails, setHistoryDetails] = useState<StudentHistoryDetailsResponse | null>(null);
  const [triggerGetStudentHistoryDetails, { isFetching: isFetchingHistoryDetails }] =
    studentsApi.useLazyGetStudentHistoryDetailsQuery();

  const toast = useRef<Toast>(null);

  const handleOpenDetails = async (history: StudentHistoryResponse) => {
    setSelectedHistory(history);
    setHistoryDetails(null);
    setShowDetailsDialog(true);

    try {
      const response = await triggerGetStudentHistoryDetails(history.studentHistoryId).unwrap();
      setHistoryDetails(response.data ?? null);
    } catch (error) {
      console.error("Failed to fetch student history details:", error);
      toast.current?.show({
        severity: "error",
        summary: "Грешка",
        detail: "Възникна грешка при извличане на детайлите за промяната.",
      });
    }
  };

  const detailRows: { field: string; value: unknown }[] = historyDetails
    ? Object.entries(historyDetails as unknown as Record<string, unknown>)
        .filter(
          ([field]) =>
            ![
              "studentHistoryId",
              "studentId",
              "studentEventId",
              "currentDisplayValue",
              "updatedDisplayValue",
              "modifiedDate",
              "modifiedByUsername",
              "documentNumber",
              "documentDate",
              "studentEventName",
              "propertyName",
              "currentValue",
              "updatedValue",
            ].includes(field),
        )
        .map(([field, value]) => ({ field, value }))
    : [];

  return (
    <>
      <Toast ref={toast} />
      <div className="field col-12">
        <Card>
          <div className="flex justify-content-start">
            <Button
              label="Добави събитие"
              icon="pi pi-plus"
              outlined
              className="w-2 mb-2"
              onClick={() => setShowEventDialog(true)}
            />
          </div>

          <Accordion>
            <AccordionTab header="История на промените на студента">
              <DataTable
                value={studentHistories}
                emptyMessage="Няма история на студента."
                stripedRows
                paginator
                rows={5}
              >
                <Column field="studentEventName" header="Събитие" />
                <Column field="propertyName" header="Свойство" />
                <Column field="currentValue" header="Стара стойност" />
                <Column field="updatedValue" header="Нова стойност" />
                <Column
                  field="modifiedDate"
                  header="Дата"
                  body={(rowData: StudentHistoryResponse) =>
                    rowData.modifiedDate ? format(new Date(rowData.modifiedDate), "dd.MM.yyyy HH:mm") : ""
                  }
                />
                <Column field="modifiedByUsername" header="Потребител" />
                <Column
                  header="Детайли"
                  body={(rowData: StudentHistoryResponse) => (
                    <Button
                      type="button"
                      icon="pi pi-eye"
                      outlined
                      size="small"
                      label="Преглед"
                      onClick={() => void handleOpenDetails(rowData)}
                    />
                  )}
                />
              </DataTable>
            </AccordionTab>
          </Accordion>
        </Card>
      </div>

      <Dialog
        header="Детайли за промяна"
        visible={showDetailsDialog}
        onHide={() => setShowDetailsDialog(false)}
        style={{ width: "60vw" }}
        modal
      >
        {!selectedHistory ? null : (
          <div className="grid">
            <div className="col-12 md:col-6">
              <strong>Събитие:</strong> {selectedHistory.studentEventName}
            </div>
            <div className="col-12 md:col-6">
              <strong>Свойство:</strong> {selectedHistory.propertyName ?? "-"}
            </div>
            <div className="col-12 md:col-6">
              <strong>Стара стойност:</strong> {selectedHistory.currentValue ?? "-"}
            </div>
            <div className="col-12 md:col-6">
              <strong>Нова стойност:</strong> {selectedHistory.updatedValue ?? "-"}
            </div>
            <div className="col-12 md:col-6">
              <strong>Стара стойност (четима):</strong> {historyDetails?.currentDisplayValue ?? "-"}
            </div>
            <div className="col-12 md:col-6">
              <strong>Нова стойност (четима):</strong> {historyDetails?.updatedDisplayValue ?? "-"}
            </div>
            <div className="col-12 md:col-6">
              <strong>Дата:</strong> {formatDateTime(selectedHistory.modifiedDate)}
            </div>
            <div className="col-12 md:col-6">
              <strong>Потребител:</strong> {selectedHistory.modifiedByUsername ?? "-"}
            </div>
            <div className="col-12 md:col-6">
              <strong>Номер на документ:</strong> {historyDetails?.documentNumber ?? "-"}
            </div>
            <div className="col-12 md:col-6">
              <strong>Дата на документ:</strong> {formatDateTime(historyDetails?.documentDate)}
            </div>

            <div className="col-12 mt-2">
              <h5 className="mb-2">Допълнителни детайли</h5>
              {isFetchingHistoryDetails && <div>Зареждане...</div>}
              {!isFetchingHistoryDetails && (!historyDetails || Object.keys(historyDetails).length === 0) && (
                <div>Няма допълнителни детайли.</div>
              )}
              {!isFetchingHistoryDetails && historyDetails && (
                <DataTable value={detailRows} size="small" stripedRows>
                  <Column field="field" header="Поле" style={{ width: "30%" }} />
                  <Column
                    field="value"
                    header="Стойност"
                    body={(rowData: { value: unknown }) => formatDetailsValue(rowData.value)}
                  />
                </DataTable>
              )}
            </div>
          </div>
        )}
      </Dialog>

      <StudentEventDialog visible={showEventDialog} onHide={() => setShowEventDialog(false)} studentId={studentId} />
    </>
  );
};

export default StudentHistorySection;
